import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

export default function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [company, setCompany] = useState(user?.company || '');
  const [packages, setPackages] = useState(user?.packages || []);
  const [allPackages, setAllPackages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/packages').then(r => setAllPackages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) { setName(user.name); setCompany(user.company || ''); setPackages(user.packages || []); }
  }, [user]);

  const togglePkg = (pkg) => {
    setPackages(prev => prev.includes(pkg) ? prev.filter(p => p !== pkg) : [...prev, pkg]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({ name, company, packages });
      toast.success('Profile updated');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const roleBadge = { admin: 'Admin', project_manager: 'Project Manager', site_manager: 'Site Manager', subcontractor: 'Subcontractor', inspector: 'Inspector' };

  return (
    <Layout>
      <div className="space-y-6" data-testid="profile-page">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Profile</h1>
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center text-white text-lg font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="font-semibold text-slate-900">{user?.name}</p>
              <p className="text-sm text-slate-500">{user?.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-[10px]">{roleBadge[user?.role]}</Badge>
                {user?.company && <Badge variant="outline" className="text-[10px]">{user.company}</Badge>}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} data-testid="profile-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Company</Label>
            <Input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company name" data-testid="profile-company-input" />
          </div>
          <div className="space-y-2">
            <Label>Assigned Packages</Label>
            <div className="grid grid-cols-1 gap-1.5 max-h-56 overflow-y-auto p-3 border rounded-lg bg-slate-50">
              {allPackages.map(pkg => (
                <label key={pkg} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white p-1.5 rounded">
                  <Checkbox checked={packages.includes(pkg)} onCheckedChange={() => togglePkg(pkg)} data-testid={`profile-pkg-${pkg.replace(/[\s/]+/g, '-').toLowerCase()}`} />
                  <span className="text-slate-700 text-xs">{pkg}</span>
                </label>
              ))}
            </div>
            {packages.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {packages.map(p => <Badge key={p} className="bg-blue-100 text-blue-700 text-[10px]">{p}</Badge>)}
              </div>
            )}
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-profile-button">
            <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
